import { useState } from 'react'
import { ActionTrigger, type ActionAs, type ActionItem } from '@/components/ActionBar'
import { memberScope } from '@/kinds/grant/schema'
import { useGrants, usePeople } from '@/db/hooks'
import { followMembers, nudgeMembersPatch } from '@/lib/follow'
import { useAccess } from '@/state/access'
import { mutate } from '@/runtime'

/**
 * "Nudge Keira": the owner's button on a task, project or list; members have
 * "Nudge Alfie" instead. It writes the `nudge_members` label (an ordinary
 * patch, so it works offline); the server decides whether it becomes a push
 * (once per item per ten minutes), so the button only says it was sent.
 */
/** A member's grant is named like "Keira's Shortcut": the first name is what the button says. */
export const memberFirstName = (name: string) =>
  (name.trim().split(/\s+/)[0] ?? '').replace(/['’]s$/i, '') || name

/** The members of a workspace who can be nudged (none: the button is not drawn). */
const useMembersOf = (ws?: string) => {
  const grants = useGrants().filter((g) => g.spec.scopes.some((sc) => memberScope(sc)?.slug === ws))
  return followMembers(grants, new Date(), usePeople())
}

export const NudgeMembersButton = ({ id, ws, as }: { id: string; ws?: string; as?: ActionAs }) => {
  const isMember = useAccess((s) => s.member)
  const members = useMembersOf(ws)
  const [sent, setSent] = useState(false)
  if (isMember || members.length === 0) return null
  const names = members.map((m) => memberFirstName(m.name)).join(' and ')
  return (
    <ActionTrigger
      as={as}
      label={sent ? 'Nudged' : `Nudge ${names}`}
      disabled={sent}
      onClick={() => {
        setSent(true)
        void mutate.patch(id, { labels: nudgeMembersPatch() })
      }}
      testid="nudge-members"
    />
  )
}

/** The same button as an ActionBar item (owner only, and only where the suite has members). */
export const useNudgeMembersItem = (id: string, ws?: string): ActionItem => {
  const isMember = useAccess((s) => s.member)
  const members = useMembersOf(ws)
  return {
    id: 'nudge-members',
    label: `Nudge ${members.map((m) => memberFirstName(m.name)).join(' and ')}`,
    hidden: isMember || members.length === 0,
    render: ({ as }) => <NudgeMembersButton id={id} ws={ws} as={as} />,
  }
}
